/**
 * Leviathan Web — person names
 * Quasar Prime LLC
 *
 * Contact names are stored as `firstName` + `lastName`, because reports sort by
 * surname and a single field cannot support that: "Fred Johns", "Fred Van Der
 * Berg" and "Dr. Fred Johns Jr." do not split by any rule that holds every time.
 *
 * Anywhere the whole name is shown — detail pages, list columns, printed
 * documents — comes through here, so a name renders the same way everywhere and
 * a missing one shows as a dash rather than as "undefined" or a blank cell.
 *
 * Mirrors `apps/api/src/shared/utils/personName.ts`. Duplicated rather than
 * shared because `packages/shared-types` is imported by nothing today; if that
 * changes, this is the first thing that should move into it.
 */

export interface PersonNameParts {
  firstName?: string | null;
  lastName?: string | null;
}

/**
 * "First Last", or whichever half exists, or `null` when there is no name.
 *
 * Returning `null` rather than `''` lets a caller tell "no contact recorded"
 * from "a contact that renders empty" — the second is a bug and should not look
 * like the first.
 */
export function fullName(parts: PersonNameParts | null | undefined): string | null {
  if (!parts) return null;
  const first = parts.firstName?.trim() || '';
  const last = parts.lastName?.trim() || '';
  const joined = [first, last].filter(Boolean).join(' ');
  return joined || null;
}

/**
 * The same, for somewhere that must render something.
 *
 * The fallback is an em dash rather than an empty string on purpose: a blank
 * where a name should be reads as a rendering fault, and this whole change
 * exists because a name can go missing in a way nobody sees.
 */
export function displayName(
  parts: PersonNameParts | null | undefined,
  fallback = '—'
): string {
  return fullName(parts) ?? fallback;
}

/** "Last, First" — for lists ordered by surname. */
export function sortableName(parts: PersonNameParts | null | undefined): string | null {
  if (!parts) return null;
  const first = parts.firstName?.trim() || '';
  const last = parts.lastName?.trim() || '';
  if (last && first) return `${last}, ${first}`;
  return last || first || null;
}
